import { type License } from "@clipboard-health/contract-worker-app-bff";
import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { TextButton } from "../../components/TextButton";
import { useLicenseRenewalWorkDocuments } from "../api/useLicenseRenewalWorkDocuments";

interface RenewCNALicenseBottomSheetProps {
  modalState: UseModalState;
  licenseAttributes: Pick<License["attributes"], "state" | "qualification">;
}

export function RenewCNALicenseBottomSheet(props: RenewCNALicenseBottomSheetProps) {
  const { modalState, licenseAttributes } = props;

  const { openArticle } = useZendeskArticle();
  const { renewCnaLicenseSupportLink } = useSupportLinks();

  const { mutate: sendWorkDocuments, isLoading: isSendingWorkDocuments } =
    useLicenseRenewalWorkDocuments({});

  return (
    <BottomSheet
      canDismiss
      disableSwipeArea={false}
      modalState={modalState}
      paperSx={(theme) => ({
        ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
      })}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isSendingWorkDocuments}
            onClick={async () => {
              logEvent(APP_V2_USER_EVENTS.EMAIL_LICENSE_RENEWAL_DOCS, {
                state: licenseAttributes.state,
              });
              sendWorkDocuments({
                state: licenseAttributes.state,
                qualification: licenseAttributes.qualification,
              });
            }}
          >
            Email work documents to myself
          </LoadingButton>
          <TextButton
            size="large"
            onClick={async () => {
              logEvent(APP_V2_USER_EVENTS.VIEW_LICENSE_RENEWAL_GUIDE_LINK, {
                state: licenseAttributes.state,
                articleLink: renewCnaLicenseSupportLink,
              });
              await openArticle(renewCnaLicenseSupportLink);
            }}
          >
            View CNA license renewal guide
          </TextButton>
        </DialogFooter>
      }
    >
      <BottomSheetIllustrationContent
        title="CNA license renewal"
        illustrationType="document-replace"
        description={
          <>
            <Text>
              To renew your <b style={{ fontWeight: "bold" }}>{licenseAttributes.state} CNA</b>{" "}
              license, you may need proof of employment from the last two years.
            </Text>
            <Box component="br" />
            <Text>
              Tap below to email yourself a work confirmation letter with your earnings summary and
              workplace history. Further instructions to renew your license will be provided in the
              email.
            </Text>
          </>
        }
      />
    </BottomSheet>
  );
}
